import React, { useEffect, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Alert, Button, Card, Space, Switch, Tag, Typography } from 'antd';
import { createInitialState, liveAssistReducer, PHASE, RECOGNITION, shouldShowPip } from './liveAssistState';
import useScreenCapture, { downloadFrame, isScreenCaptureSupported } from './useScreenCapture';
import usePip, { isDocumentPipSupported } from './usePip';
import PipPanel from './PipPanel';
import PipTeamsView from './PipTeamsView';
import PipMapPanel from './PipMapPanel';
import useRouteDestinations from './useRouteDestinations';
import useCompositionRecognizer from './recognition/useCompositionRecognizer';
import useCurrentPhase from './gameClock/useCurrentPhase';
import MyTeamCurveCard from './MyTeamCurveCard';
import GameLogCard from './gameStatus/GameLogCard';
import useStatusLog from './gameStatus/useStatusLog';
import useWickelineWatch from './gameEvents/useWickelineWatch';
import useTopRankerStats from './useTopRankerStats';

const timeLabel = (date) => date.toLocaleTimeString('ko-KR', { hour12: false });

// "게임 중에도 PiP 유지" 설정은 브라우저에 기억한다 (다중 모니터 사용자는 한 번만 켜면 됨). 저장소를 못 쓰는 환경이면 기본값(true)을 쓴다.
const PIP_IN_GAME_KEY = 'bserLiveAssist.pipInGame';
const loadPipInGame = () => {
  try {
    const saved = window.localStorage.getItem(PIP_IN_GAME_KEY);
    return saved === null ? true : saved === 'true';
  } catch (e) {
    return true;
  }
};
const savePipInGame = (enabled) => {
  try {
    window.localStorage.setItem(PIP_IN_GAME_KEY, String(enabled));
  } catch (e) {
    // 저장 실패는 무시 (이번 세션에는 적용됨)
  }
};

/**
 * 이리 라이브 도우미 (1단계: 화면 캡처 + PiP 게임 사이클).
 *
 * 1) 대기: PiP에 [조합 캡처] [게임 시작]만 둔다.
 * 2) [조합 캡처]: 공유 중인 게임 창의 프레임을 캡처한다 (인식/통계 표시는 다음 단계).
 * 3) [게임 시작]: 단일 모니터면 PiP를 닫고 정보는 웹에서만 보여준다. 다중 모니터(기본)면 PiP를 게임이 없는 모니터에 두고 계속 보여준다.
 * 4) 웹의 [게임 종료]: 대기로 돌아오고 (닫혀 있었다면) PiP를 다시 보여준다. 웹에서 PiP를 숨길 수도 있다.
 */
export default function LiveAssist() {
  const [state, dispatch] = useReducer(liveAssistReducer, undefined, () => createInitialState({ pipInGame: loadPipInGame() }));
  const capture = useScreenCapture();
  const recognizer = useCompositionRecognizer();
  const topRanker = useTopRankerStats();
  const [notice, setNotice] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [gameRun, setGameRun] = useState(0); // 게임 시작을 누를 때마다 늘어난다(새 게임이면 게임 로그를 비움)

  const pipVisible = shouldShowPip(state);
  const pip = usePip({
    visible: pipVisible,
    // 사용자가 PiP를 직접 닫았다면 숨김으로 보고 임의로 다시 열지 않는다
    onClosedByUser: () => dispatch({ type: 'PIP_HIDDEN_CHANGED', hidden: true }),
    // 모니터가 여러 개면 팀별 정보를 다 보여줄 큰 창, 하나뿐이면 작은 버튼 창
    ...(state.pipInGame ? { width: 960, height: 720 } : {}),
  });

  // 조합 캡처가 교체되거나 비워질 때 이전 이미지 URL을 해제한다
  const shownFrameRef = useRef(null);
  useEffect(() => {
    const previous = shownFrameRef.current;
    if (previous && previous !== state.compFrame) {
      URL.revokeObjectURL(previous.url);
    }
    shownFrameRef.current = state.compFrame;
  }, [state.compFrame]);
  useEffect(() => () => {
    if (shownFrameRef.current) {
      URL.revokeObjectURL(shownFrameRef.current.url);
    }
  }, []);

  const handleCaptureComp = async () => {
    setCapturing(true);
    try {
      const frame = await capture.captureFrame();
      dispatch({ type: 'COMP_CAPTURED', frame });
      setNotice(`조합 캡처 완료 (${timeLabel(frame.capturedAt)}, ${frame.width}×${frame.height})`);
      dispatch({ type: 'RECOGNITION_STARTED' });
      recognizer.recognize(frame.blob)
        .then((teams) => dispatch({ type: 'RECOGNITION_SUCCEEDED', teams }))
        .catch((e) => dispatch({ type: 'RECOGNITION_FAILED', message: e.message }));
    } catch (e) {
      setNotice(e.message);
    } finally {
      setCapturing(false);
    }
  };

  const inGame = state.phase === PHASE.IN_GAME;
  const currentPhase = useCurrentPhase({ enabled: inGame && capture.sharing, captureClockImage: capture.captureClockImage });
  const statusLog = useStatusLog({
    enabled: inGame && capture.sharing,
    captureStatusImage: capture.captureStatusImage,
    captureGameTimeImage: capture.captureGameTimeImage,
    phaseLabel: currentPhase.label,
    resetToken: gameRun,
  });
  const wickelineWatch = useWickelineWatch({
    enabled: inGame && capture.sharing,
    captureWickelineImage: capture.captureWickelineImage,
    resetToken: gameRun,
    onKill: statusLog.recordWickelineKill,
  });
  const recognition = state.recognition;
  const routeDestinations = useRouteDestinations({
    teams: recognition.status === RECOGNITION.DONE ? recognition.teams : null,
    stats: topRanker.stats,
  });
  const handleSelectTeam = (index) => dispatch({ type: 'TEAM_SELECTED', index });
  const recognitionStatusText = recognition.status === RECOGNITION.RUNNING
    ? `조합 인식 중… ${recognizer.progress ? `${recognizer.progress.done}/${recognizer.progress.total}` : recognizer.status || ''}`
    : recognition.status === RECOGNITION.ERROR ? `조합 인식 실패: ${recognition.error}` : '';
  const teamsView = recognition.status === RECOGNITION.DONE && recognition.teams ? (
    <PipTeamsView
      teams={recognition.teams}
      selectedTeam={state.selectedTeam}
      onSelectTeam={handleSelectTeam}
      topRanker={topRanker}
      currentPhase={currentPhase.phase}
    />
  ) : null;

  const handleStartGame = () => {
    setGameRun((n) => n + 1);
    dispatch({ type: 'GAME_STARTED' });
    setNotice('');
  };

  const handleEndGame = () => {
    dispatch({ type: 'GAME_ENDED' });
    setNotice('');
    // PiP는 사용자 클릭 안에서만 열 수 있어서 이 클릭 핸들러에서 연다 (숨김 상태가 아닐 때만)
    if (!state.pipHidden) {
      pip.open();
    }
  };

  const handleTogglePip = () => {
    if (pip.pipWindow) {
      dispatch({ type: 'PIP_HIDDEN_CHANGED', hidden: true });
      return;
    }
    dispatch({ type: 'PIP_HIDDEN_CHANGED', hidden: false });
    pip.open();
  };

  const handleTogglePipMaximize = () => {
    if (!pip.toggleMaximize(setNotice)) {
      setNotice('이 브라우저에서는 PiP 창을 최대화할 수 없어요. 창 가장자리를 끌어 키워 주세요.');
    }
  };

  const handlePipInGameChange = (enabled) => {
    dispatch({ type: 'PIP_IN_GAME_CHANGED', enabled });
    savePipInGame(enabled);
    // 게임 중에 켜는 경우, 닫혀 있던 PiP를 이 클릭 안에서 다시 연다 (숨김 상태가 아닐 때만)
    if (enabled && !state.pipHidden && !pip.pipWindow) {
      pip.open();
    }
  };

  const handleSaveCurrent = async () => {
    try {
      const frame = await capture.captureFrame();
      downloadFrame(frame, 'bser-frame');
      URL.revokeObjectURL(frame.url);
    } catch (e) {
      setNotice(e.message);
    }
  };

  const compFrame = state.compFrame;

  const pipPortal = pip.pipWindow &&
  createPortal(
    <PipPanel
      onCaptureComp={handleCaptureComp}
      onStartGame={handleStartGame}
      notice={recognitionStatusText || notice}
      busy={capturing || recognition.status === RECOGNITION.RUNNING}
      phaseLabel={currentPhase.label}
      maximized={pip.maximized}
      onToggleMaximize={handleTogglePipMaximize}
      side={teamsView ? <PipMapPanel entries={routeDestinations.entries} current={currentPhase.phase} /> : null}
    >
      {teamsView}
    </PipPanel>,
    pip.pipWindow.document.body
  );

  // 게임 중에는 꼭 필요한 것만: 상단 상태 줄 + 왼쪽 위 우리 팀 파워 커브(항상 표시) + 오른쪽 전체 게임 로그
  if (inGame) {
    return (
      <div style={{ maxWidth: 1600 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <Typography.Title level={5} style={{ margin: 0 }}>화면 도우미</Typography.Title>
          <Tag color="red" style={{ margin: 0 }}>게임 중</Tag>
          <Tag color={capture.sharing ? 'green' : 'orange'} style={{ margin: 0 }}>{capture.sharing ? '화면 공유 중' : '화면 공유 끊김'}</Tag>
          {currentPhase.label
            ? <span style={{ fontSize: 15 }}>지금 <b>{currentPhase.label}</b></span>
            : <Typography.Text type="secondary" style={{ fontSize: 13 }}>일차/낮밤 인식 중…</Typography.Text>}
          <Space style={{ marginLeft: 'auto' }} wrap>
            {!capture.sharing && <Button type="primary" onClick={capture.start}>게임 창 공유 시작</Button>}
            <Button
              onClick={handleTogglePip}
              disabled={!pip.supported || !state.pipInGame}
              title={!state.pipInGame ? '게임 중에는 PiP가 표시되지 않아요 (게임 종료 후 설정에서 바꿀 수 있어요)' : undefined}
            >
              {pip.pipWindow ? 'PiP 숨김' : 'PiP 보이기'}
            </Button>
            <Button danger onClick={handleEndGame}>게임 종료</Button>
          </Space>
          {/* 공유 화면 수신용 video(끊기지 않게 항상 둔다). 게임 중에는 작은 미리보기만 */}
          <video
            ref={capture.attachVideo}
            muted
            playsInline
            style={{ display: capture.sharing ? 'block' : 'none', width: 120, background: '#000', borderRadius: 4 }}
          />
        </div>
        {capture.error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={capture.error} />}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 520px', minWidth: 0, position: 'sticky', top: 8 }}>
            <MyTeamCurveCard
              teams={recognition.status === RECOGNITION.DONE ? recognition.teams : null}
              topRanker={topRanker}
              currentPhase={currentPhase.phase}
            />
          </div>
          <div style={{ flex: '1 1 380px', minWidth: 0 }}>
            <GameLogCard
              entries={statusLog.entries}
              summary={statusLog.summary}
              wickelineKill={statusLog.wickelineKill}
              wickelineDiag={wickelineWatch.diag}
              onSaveKillFrames={wickelineWatch.saveFrames}
              active={inGame && capture.sharing}
              fill
            />
          </div>
        </div>
        {pipPortal}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960 }}>
      <Typography.Title level={4}>화면 도우미</Typography.Title>
      <Typography.Paragraph type="secondary">
        이터널 리턴 게임 창을 화면 공유로 받아 정보를 보여주는 도우미입니다. 게임 프로세스에는 접근하지 않고 브라우저가 받은 화면
        영상만 사용합니다. 조합 창을 캡처하면 카드(캐릭터+무기)를 인식해 팀별 통계와 파워 커브를 보여줍니다.
      </Typography.Paragraph>

      {!isScreenCaptureSupported() && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="이 브라우저는 화면 공유를 지원하지 않아요. PC의 크롬/엣지에서 열어 주세요."
        />
      )}
      {isScreenCaptureSupported() && !isDocumentPipSupported() && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="이 브라우저는 PiP(항상 위 작은 창)를 지원하지 않아요. 크롬/엣지 116 이상에서 쓸 수 있고, 지금은 아래 웹 버튼으로 같은 순서를 진행할 수 있어요."
        />
      )}
      {capture.error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={capture.error} />}

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card
          size="small"
          title="1. 게임 화면 공유"
          extra={<Tag color={capture.sharing ? 'green' : 'default'}>{capture.sharing ? '공유 중' : '공유 안 함'}</Tag>}
        >
          <Space wrap>
            {capture.sharing ? (
              <Button onClick={capture.stop}>공유 중지</Button>
            ) : (
              <Button type="primary" onClick={capture.start}>게임 창 공유 시작</Button>
            )}
            <Button onClick={handleSaveCurrent} disabled={!capture.sharing}>
              현재 화면 저장 (샘플 수집)
            </Button>
          </Space>
          <Typography.Paragraph type="secondary" style={{ margin: '8px 0 0' }}>
            공유 창에서 <b>"창"</b> 탭의 이터널 리턴을 고르세요. 테두리 없는 창모드에서 가장 잘 잡히고, 독점 전체화면은 캡처가
            불안정할 수 있어요.
          </Typography.Paragraph>
          {/* 공유 화면 수신용 video. 공유 중에만 작게 미리보기로 보여준다 */}
          <video
            ref={capture.attachVideo}
            muted
            playsInline
            style={{
              display: capture.sharing ? 'block' : 'none',
              marginTop: 8,
              width: 320,
              maxWidth: '100%',
              background: '#000',
              borderRadius: 4,
            }}
          />
        </Card>

        <Card
          size="small"
          title="2. 게임 사이클"
          extra={<Tag color={inGame ? 'red' : 'blue'}>{inGame ? '게임 중' : '대기 중'}</Tag>}
        >
          <Space wrap>
            {inGame ? (
              <Button danger onClick={handleEndGame}>게임 종료</Button>
            ) : (
              <>
                <Button type="primary" onClick={handleCaptureComp} loading={capturing} disabled={!capture.sharing || recognition.status === RECOGNITION.RUNNING}>
                  조합 캡처
                </Button>
                <Button onClick={handleStartGame}>게임 시작</Button>
              </>
            )}
            <Button
              onClick={handleTogglePip}
              disabled={!pip.supported || (inGame && !state.pipInGame)}
              title={inGame && !state.pipInGame ? '게임 중에는 PiP가 표시되지 않아요 (아래 설정에서 바꿀 수 있어요)' : undefined}
            >
              {pip.pipWindow ? 'PiP 숨김' : 'PiP 보이기'}
            </Button>
          </Space>
          <div style={{ marginTop: 10 }}>
            <Space>
              <Switch checked={state.pipInGame} onChange={handlePipInGameChange} />
              <span>게임 중에도 PiP 유지 <Typography.Text type="secondary">(모니터가 2개 이상일 때 권장)</Typography.Text></span>
            </Space>
          </div>
          <Typography.Paragraph type="secondary" style={{ margin: '8px 0 0' }}>
            PiP는 클릭이 게임으로 통과되지 않으니 <b>게임이 없는 다른 모니터로 옮겨 두세요</b>(한 번 옮기면 위치를 기억해요). 모니터가 하나뿐이면 위 설정을
            끄세요. 그러면 [게임 시작]을 누를 때 PiP가 닫혀 게임 클릭을 방해하지 않아요. 게임이 끝나면 여기서 [게임 종료]를 눌러 다시 대기로 돌아오세요.
          </Typography.Paragraph>
          {inGame && (
            <Typography.Paragraph style={{ margin: '8px 0 0' }}>
              {currentPhase.label
                ? <span>지금 <b>{currentPhase.label}</b></span>
                : <Typography.Text type="secondary">일차/낮밤 인식 중… (화면 위 가운데의 일차와 해/달 아이콘이 보여야 해요)</Typography.Text>}
            </Typography.Paragraph>
          )}
          {notice && <Alert type="info" showIcon style={{ marginTop: 8 }} message={notice} />}
        </Card>

        <GameLogCard
          entries={statusLog.entries}
          summary={statusLog.summary}
          wickelineKill={statusLog.wickelineKill}
          active={inGame && capture.sharing}
        />

        <Card size="small" title="조합 분석">
          {recognitionStatusText && (
            <Alert type={recognition.status === RECOGNITION.ERROR ? 'error' : 'info'} showIcon style={{ marginBottom: 8 }} message={recognitionStatusText} />
          )}
          {teamsView ? (
            <div style={{ background: '#1f1f2e', color: '#d9d9d9', borderRadius: 6, padding: 8, fontFamily: "'Pretendard', 'Malgun Gothic', sans-serif" }}>
              {teamsView}
            </div>
          ) : (
            !recognitionStatusText && (
              <Typography.Text type="secondary">
                조합 창이 보이는 상태에서 [조합 캡처]를 누르면 팀별 카드를 인식해 통계를 보여줘요. 팀을 누르면 파워 커브를 겹쳐 볼 수 있어요.
              </Typography.Text>
            )
          )}
        </Card>

        <Card size="small" title="조합 캡처">
          {compFrame ? (
            <Space direction="vertical" size={8}>
              <img
                src={compFrame.url}
                alt="조합 캡처"
                style={{ maxWidth: '100%', width: 480, border: '1px solid #f0f0f0', borderRadius: 4 }}
              />
              <Space wrap>
                <Typography.Text type="secondary">
                  {timeLabel(compFrame.capturedAt)} · {compFrame.width}×{compFrame.height}
                </Typography.Text>
                <Button size="small" onClick={() => downloadFrame(compFrame, 'bser-comp')}>이 캡처 저장</Button>
              </Space>
            </Space>
          ) : (
            <Typography.Text type="secondary">
              아직 캡처한 조합이 없어요. 조합 창이 보이는 상태에서 [조합 캡처]를 누르세요.
            </Typography.Text>
          )}
        </Card>
      </Space>

      {pipPortal}
    </div>
  );
}
